import { useEffect, useState } from 'react';
import { ShieldCheck, UserX, AlertCircle, Send, CheckCircle } from 'lucide-react';
import { supabase } from '../services/supabase';
import Confirm from '../components/ui/Confirm';

// Fila que devuelve el RPC admin_list_directors (mig 182). `has_accepted` sale
// de auth.users (email_confirmed_at IS NOT NULL) — no está en profiles ni es
// accesible desde el cliente, por eso pasa por un RPC root-only.
type DirectorRow = {
  id: string;
  email: string;
  full_name: string;
  active: boolean;
  has_accepted: boolean;
};

// Tres estados derivados (decisión de producto M):
//   · Activo    → active=true  Y  ha aceptado la invitación
//   · Pendiente → active=true  pero AÚN no ha aceptado (limbo que antes se
//                 mostraba como "Activo" sin poder entrar)
//   · Revocado  → active=false
type DirectorState = 'activo' | 'pendiente' | 'revocado';
function stateOf(d: DirectorRow): DirectorState {
  if (!d.active) return 'revocado';
  return d.has_accepted ? 'activo' : 'pendiente';
}

// Página ROOT-ONLY (gated por RootAdminOnly en App.tsx + RPC/EF root-only).
// Lista los directores (vía RPC admin_list_directors, mig 182) y permite:
//   · REVOCAR (active=true → false) vía RPC admin_deactivate_director (mig 145).
//     Disponible en Activo y Pendiente (ambos active=true).
//   · REINVITAR vía EF reinvite-director (root-only), en Pendiente y Revocado:
//       - Revocado: reactiva (active=true) + reenvía el enlace, atómico.
//       - Pendiente: la cuenta ya está active=true, así que en la práctica solo
//         reenvía el enlace (el RPC de reactivación es idempotente).
//     La EF manda un email de recovery a /set-password.
// La creación de directores va por "Crear director" del Header (EF create-director).
export default function DirectoresPage() {
  const [directores, setDirectores] = useState<DirectorRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [confirmRevoke, setConfirmRevoke] = useState<DirectorRow | null>(null);
  const [revoking, setRevoking] = useState(false);
  const [confirmReinvite, setConfirmReinvite] = useState<DirectorRow | null>(null);
  const [reinviting, setReinviting] = useState(false);

  async function load() {
    setLoading(true);
    const { data, error: err } = await supabase.rpc('admin_list_directors');
    if (err) setError(err.message);
    else setDirectores((data as DirectorRow[]) ?? []);
    setLoading(false);
  }

  useEffect(() => {
    void load();
  }, []);

  async function handleRevoke() {
    if (!confirmRevoke) return;
    setRevoking(true);
    setError('');
    setSuccess('');
    const { error: err } = await supabase.rpc('admin_deactivate_director', {
      p_user_id: confirmRevoke.id,
    });
    setRevoking(false);
    if (err) {
      setError(err.message);
    } else {
      setDirectores((prev) =>
        prev.map((d) => (d.id === confirmRevoke.id ? { ...d, active: false } : d))
      );
    }
  }

  async function handleReinvite() {
    // Capturamos el objetivo ANTES del primer await: Confirm cierra el diálogo
    // (setConfirmReinvite(null)) justo tras invocar onConfirm.
    const target = confirmReinvite;
    if (!target) return;
    setReinviting(true);
    setError('');
    setSuccess('');
    try {
      const redirectTo = `${window.location.origin}/set-password`;
      const { data, error: err } = await supabase.functions.invoke('reinvite-director', {
        body: { user_id: target.id, redirect_to: redirectTo },
      });
      if (err) {
        // El cuerpo JSON con nuestro { error } viene en error.context (patrón de
        // Header.handleCreateDirector).
        let msg = 'Error al reinvitar al director';
        try {
          const ctx = (err as { context?: { json?: () => Promise<{ error?: string }> } }).context;
          const body = await ctx?.json?.();
          msg = body?.error ?? err.message;
        } catch {
          msg = err.message;
        }
        throw new Error(msg);
      }
      if (data?.error) throw new Error(data.error);
      // Reflejamos active=true en la fila. Para un Pendiente ya era true
      // (no-op → sigue Pendiente hasta que acepte). Para un Revocado pasa a
      // Pendiente si nunca aceptó, o a Activo si ya había aceptado antes.
      const wasPending = target.active && !target.has_accepted;
      setDirectores((prev) => prev.map((d) => (d.id === target.id ? { ...d, active: true } : d)));
      setSuccess(
        wasPending
          ? `Invitación reenviada a ${target.email}.`
          : `Invitación enviada a ${target.email}. La cuenta queda reactivada.`
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Error al reinvitar al director');
    } finally {
      setReinviting(false);
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <ShieldCheck className="text-gold-primary" size={24} />
        <div>
          <h2 className="font-oswald text-2xl uppercase tracking-wide text-white">Directores</h2>
          <p className="text-text-secondary text-sm font-inter">
            Acceso pleno como administrador. Solo el administrador raíz puede crear, revocar o
            reinvitar directores.
          </p>
        </div>
      </div>

      {error && (
        <div
          className="flex items-center gap-2 text-error text-sm font-inter"
          data-testid="dir-error"
        >
          <AlertCircle size={16} /> {error}
        </div>
      )}

      {success && (
        <div
          className="flex items-center gap-2 text-success text-sm font-inter"
          data-testid="dir-success"
        >
          <CheckCircle size={16} /> {success}
        </div>
      )}

      {loading ? (
        <p className="text-text-secondary font-inter">Cargando…</p>
      ) : directores.length === 0 ? (
        <p className="text-text-secondary font-inter" data-testid="dir-empty">
          No hay directores. Usa “Crear director” en el menú superior para invitar a uno.
        </p>
      ) : (
        <div className="bg-bg-surface border border-bg-border rounded-xl overflow-hidden">
          <table className="w-full text-sm font-inter">
            <thead>
              <tr className="text-text-secondary text-left border-b border-bg-border">
                <th className="px-4 py-3 font-medium">Nombre</th>
                <th className="px-4 py-3 font-medium">Email</th>
                <th className="px-4 py-3 font-medium">Estado</th>
                <th className="px-4 py-3 font-medium text-right">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {directores.map((d) => (
                <tr key={d.id} className="border-b border-bg-border/50" data-testid="dir-row">
                  <td className="px-4 py-3 text-white">{d.full_name}</td>
                  <td className="px-4 py-3 text-text-secondary">{d.email}</td>
                  <td className="px-4 py-3">
                    {stateOf(d) === 'activo' ? (
                      <span className="text-success">Activo</span>
                    ) : stateOf(d) === 'pendiente' ? (
                      <span className="text-warning">Pendiente</span>
                    ) : (
                      <span className="text-text-secondary">Revocado</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="inline-flex items-center justify-end gap-4">
                      {/* Reinvitar: Pendiente (solo reenvía) y Revocado (reactiva
                          + reenvía). En Activo no va: se resuelve con "he
                          olvidado mi contraseña" desde el login. */}
                      {stateOf(d) !== 'activo' && (
                        <button
                          onClick={() => setConfirmReinvite(d)}
                          disabled={reinviting}
                          className="inline-flex items-center gap-1.5 text-gold-primary hover:opacity-80 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
                          data-testid="dir-reinvite"
                        >
                          <Send size={15} /> Reinvitar
                        </button>
                      )}
                      {/* Revocar: cualquier cuenta activa (Activo o Pendiente). */}
                      {d.active && (
                        <button
                          onClick={() => setConfirmRevoke(d)}
                          className="inline-flex items-center gap-1.5 text-error hover:opacity-80 transition-opacity"
                          data-testid="dir-revoke"
                        >
                          <UserX size={15} /> Revocar
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Confirm
        open={!!confirmRevoke}
        title="Revocar director"
        message={`¿Revocar a ${confirmRevoke?.full_name}? Su cuenta quedará desactivada y no podrá acceder. El rol se conserva, pero pierde el acceso de inmediato.`}
        confirmLabel={revoking ? 'Revocando…' : 'Revocar'}
        onConfirm={handleRevoke}
        onClose={() => setConfirmRevoke(null)}
        danger
      />

      <Confirm
        open={!!confirmReinvite}
        title={
          confirmReinvite && confirmReinvite.active ? 'Reenviar invitación' : 'Reinvitar director'
        }
        message={
          confirmReinvite && confirmReinvite.active
            ? // Pendiente: la cuenta ya está activa → solo se reenvía el enlace.
              `¿Reenviar la invitación a ${confirmReinvite?.full_name}? Se le enviará de nuevo el email para definir su contraseña y acceder. Su cuenta ya está activa.`
            : // Revocado: se reactiva y se reenvía el enlace.
              `¿Reinvitar a ${confirmReinvite?.full_name}? Se reactivará su cuenta y se le enviará un email para definir su contraseña de nuevo.`
        }
        confirmLabel={reinviting ? 'Enviando…' : confirmReinvite?.active ? 'Reenviar' : 'Reinvitar'}
        onConfirm={handleReinvite}
        onClose={() => setConfirmReinvite(null)}
      />
    </div>
  );
}
